import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { TradePlanInputSchema, type TradePlanInput } from 'shared';

import type { AccountDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

export const CHECKLIST_ITEMS = [
  ['thesisStated', 'Thesis stated'],
  ['whyNowStated', 'Why now is clear'],
  ['stopLevelAndReason', 'Stop level and reason'],
  ['targetLevelAndReason', 'Target level and reason'],
  ['positionSizeJustified', 'Position size justified'],
  ['themeCorrelationChecked', 'Theme/correlation checked'],
  ['recentSymbolTradeReviewed', 'Recent symbol trades reviewed'],
  ['notRevengeTrading', 'Not trying to make back a loss'],
  ['fomoContextChecked', 'Noise/FOMO context checked'],
  ['invalidationDefined', 'Invalidation defined'],
  ['maxAddDaysPlanned', 'Max add days planned'],
] as const;

interface TradePlanFormProps {
  accounts: AccountDto[];
  isSubmitting?: boolean;
  onSubmit: (input: TradePlanInput) => void;
  onChange?: (input: TradePlanDraft) => void;
  disabled?: boolean;
  requiresOverride?: boolean;
  recommendedQty?: number;
}

export type TradePlanDraft = Partial<Omit<TradePlanInput, 'checklist'>> & {
  checklist?: Record<string, boolean | undefined> | undefined;
};

const numberValue = (value: unknown) => {
  if (value === '') return undefined;
  return typeof value === 'string' ? Number(value) : value;
};

const optionalText = (value: unknown) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
};

export function TradePlanForm({
  accounts,
  isSubmitting = false,
  onSubmit,
  onChange,
  disabled = false,
  requiresOverride = false,
  recommendedQty,
}: TradePlanFormProps) {
  const defaultChecklist = useMemo(
    () => Object.fromEntries(CHECKLIST_ITEMS.map(([key]) => [key, false])),
    [],
  );
  const form = useForm<TradePlanInput>({
    resolver: zodResolver(TradePlanInputSchema),
    mode: 'onChange',
    defaultValues: {
      accountId: accounts[0]?.id ?? '',
      symbol: '',
      side: 'BUY',
      tradeType: 'SWING',
      conviction: 2,
      thesis: '',
      invalidation: '',
      isFtdPilot: false,
      checklist: defaultChecklist,
    },
  });

  const checklist = form.watch('checklist') ?? {};
  const checklistComplete = CHECKLIST_ITEMS.every(([key]) => checklist[key] === true);
  const { errors } = form.formState;
  const submitLocked = useRef(false);

  useEffect(() => {
    const subscription = form.watch((value) => {
      onChange?.(value);
    });
    return () => subscription.unsubscribe();
  }, [form, onChange]);

  useEffect(() => {
    const selectedAccountId = form.getValues('accountId');
    if (accounts.some((account) => account.id === selectedAccountId)) return;
    form.setValue('accountId', accounts[0]?.id ?? '', {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }, [accounts, form]);

  useEffect(() => {
    if (recommendedQty === undefined) return;
    form.setValue('proposedQty', recommendedQty, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }, [form, recommendedQty]);

  useEffect(() => {
    if (!isSubmitting) submitLocked.current = false;
  }, [isSubmitting]);

  const submit: SubmitHandler<TradePlanInput> = (input) => {
    if (!checklistComplete || submitLocked.current) return;
    submitLocked.current = true;
    onSubmit({ ...input, symbol: input.symbol.toUpperCase() });
  };

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(submit)}>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          Account
          <select
            className="mt-1 h-10 w-full rounded border border-border bg-bg px-3 text-sm"
            aria-label="Account"
            {...form.register('accountId')}
          >
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm" htmlFor="trade-plan-symbol">
          Symbol
          <Input
            id="trade-plan-symbol"
            aria-label="Symbol"
            aria-invalid={errors.symbol ? 'true' : 'false'}
            aria-describedby={errors.symbol ? 'trade-plan-symbol-error' : undefined}
            {...form.register('symbol', {
              setValueAs: (value: unknown) =>
                typeof value === 'string' ? value.trim().toUpperCase() : value,
            })}
          />
          <FieldError id="trade-plan-symbol-error" message={errors.symbol?.message} />
        </label>
        <label className="text-sm">
          Side
          <select
            className="mt-1 h-10 w-full rounded border border-border bg-bg px-3 text-sm"
            aria-label="Side"
            {...form.register('side')}
          >
            <option value="BUY">BUY</option>
            <option value="SELL">SELL</option>
          </select>
        </label>
        <label className="text-sm">
          Trade type
          <select
            className="mt-1 h-10 w-full rounded border border-border bg-bg px-3 text-sm"
            aria-label="Trade type"
            {...form.register('tradeType')}
          >
            <option value="SCALP">SCALP</option>
            <option value="SWING">SWING</option>
            <option value="INVEST">INVEST</option>
          </select>
        </label>
        <label className="text-sm" htmlFor="trade-plan-entry">
          Entry
          <Input
            id="trade-plan-entry"
            aria-label="Entry"
            type="number"
            step="0.01"
            aria-invalid={errors.proposedEntry ? 'true' : 'false'}
            aria-describedby={errors.proposedEntry ? 'trade-plan-entry-error' : undefined}
            {...form.register('proposedEntry', { valueAsNumber: true })}
          />
          <FieldError id="trade-plan-entry-error" message={errors.proposedEntry?.message} />
        </label>
        <label className="text-sm" htmlFor="trade-plan-stop">
          Stop
          <Input
            id="trade-plan-stop"
            aria-label="Stop"
            type="number"
            step="0.01"
            aria-invalid={errors.proposedStop ? 'true' : 'false'}
            aria-describedby={errors.proposedStop ? 'trade-plan-stop-error' : undefined}
            {...form.register('proposedStop', { setValueAs: numberValue })}
          />
          <FieldError id="trade-plan-stop-error" message={errors.proposedStop?.message} />
        </label>
        <label className="text-sm" htmlFor="trade-plan-entry-min">
          Planned entry minimum
          <Input
            id="trade-plan-entry-min"
            aria-label="Planned entry minimum"
            type="number"
            step="0.01"
            aria-invalid={errors.plannedEntryMin ? 'true' : 'false'}
            aria-describedby={errors.plannedEntryMin ? 'trade-plan-entry-min-error' : undefined}
            {...form.register('plannedEntryMin', { setValueAs: numberValue })}
          />
          <FieldError id="trade-plan-entry-min-error" message={errors.plannedEntryMin?.message} />
        </label>
        <label className="text-sm" htmlFor="trade-plan-entry-max">
          Planned entry maximum
          <Input
            id="trade-plan-entry-max"
            aria-label="Planned entry maximum"
            type="number"
            step="0.01"
            aria-invalid={errors.plannedEntryMax ? 'true' : 'false'}
            aria-describedby={errors.plannedEntryMax ? 'trade-plan-entry-max-error' : undefined}
            {...form.register('plannedEntryMax', { setValueAs: numberValue })}
          />
          <FieldError id="trade-plan-entry-max-error" message={errors.plannedEntryMax?.message} />
        </label>
        <label className="text-sm" htmlFor="trade-plan-target">
          Target
          <Input
            id="trade-plan-target"
            aria-label="Target"
            type="number"
            step="0.01"
            aria-invalid={errors.proposedTarget ? 'true' : 'false'}
            aria-describedby={errors.proposedTarget ? 'trade-plan-target-error' : undefined}
            {...form.register('proposedTarget', { setValueAs: numberValue })}
          />
          <FieldError id="trade-plan-target-error" message={errors.proposedTarget?.message} />
        </label>
        <label className="text-sm" htmlFor="trade-plan-quantity">
          Quantity (automatic max)
          <Input
            id="trade-plan-quantity"
            aria-label="Quantity"
            type="number"
            step="1"
            readOnly
            aria-invalid={errors.proposedQty ? 'true' : 'false'}
            aria-describedby="trade-plan-quantity-help"
            {...form.register('proposedQty', { setValueAs: numberValue })}
          />
          <p id="trade-plan-quantity-help" className="mt-1 text-xs text-text-muted">
            Locked to the gate&apos;s maximum quantity from your stop distance, risk tier and
            position cap.
          </p>
          <FieldError id="trade-plan-quantity-error" message={errors.proposedQty?.message} />
        </label>
        <label className="text-sm" htmlFor="trade-plan-theme">
          Theme
          <Input
            id="trade-plan-theme"
            aria-label="Theme"
            aria-invalid={errors.theme ? 'true' : 'false'}
            aria-describedby={errors.theme ? 'trade-plan-theme-error' : undefined}
            {...form.register('theme', { setValueAs: optionalText })}
          />
          <FieldError id="trade-plan-theme-error" message={errors.theme?.message} />
        </label>
        <label className="text-sm" htmlFor="trade-plan-conviction">
          Conviction
          <Input
            id="trade-plan-conviction"
            aria-label="Conviction"
            type="number"
            min={1}
            max={3}
            aria-invalid={errors.conviction ? 'true' : 'false'}
            aria-describedby={errors.conviction ? 'trade-plan-conviction-error' : undefined}
            {...form.register('conviction', { valueAsNumber: true })}
          />
          <FieldError id="trade-plan-conviction-error" message={errors.conviction?.message} />
        </label>
        <label className="flex items-center gap-2 rounded border border-border px-3 py-2 text-sm">
          <input type="checkbox" {...form.register('isFtdPilot')} />
          <span>FTD pilot entry</span>
        </label>
      </div>

      <label className="block text-sm" htmlFor="trade-plan-thesis">
        Thesis
        <textarea
          id="trade-plan-thesis"
          className="mt-1 min-h-24 w-full rounded border border-border bg-bg px-3 py-2 text-sm"
          aria-label="Thesis"
          aria-invalid={errors.thesis ? 'true' : 'false'}
          aria-describedby={errors.thesis ? 'trade-plan-thesis-error' : undefined}
          {...form.register('thesis')}
        />
        <FieldError id="trade-plan-thesis-error" message={errors.thesis?.message} />
      </label>

      <label className="block text-sm" htmlFor="trade-plan-why-now">
        Why now
        <textarea
          id="trade-plan-why-now"
          className="mt-1 min-h-20 w-full rounded border border-border bg-bg px-3 py-2 text-sm"
          aria-label="Why now"
          aria-invalid={errors.whyNow ? 'true' : 'false'}
          aria-describedby={errors.whyNow ? 'trade-plan-why-now-error' : undefined}
          {...form.register('whyNow', { setValueAs: optionalText })}
        />
        <FieldError id="trade-plan-why-now-error" message={errors.whyNow?.message} />
      </label>

      <label className="block text-sm" htmlFor="trade-plan-invalidation">
        Invalidation
        <textarea
          id="trade-plan-invalidation"
          className="mt-1 min-h-20 w-full rounded border border-border bg-bg px-3 py-2 text-sm"
          aria-label="Invalidation"
          aria-invalid={errors.invalidation ? 'true' : 'false'}
          aria-describedby={errors.invalidation ? 'trade-plan-invalidation-error' : undefined}
          {...form.register('invalidation', { setValueAs: optionalText })}
        />
        <FieldError id="trade-plan-invalidation-error" message={errors.invalidation?.message} />
      </label>

      {requiresOverride ? (
        <label className="block text-sm" htmlFor="trade-plan-override">
          Soft-warning override reason
          <textarea
            id="trade-plan-override"
            className="mt-1 min-h-20 w-full rounded border border-warn bg-bg px-3 py-2 text-sm"
            aria-label="Soft-warning override reason"
            aria-invalid={errors.overrideReason ? 'true' : 'false'}
            aria-describedby={errors.overrideReason ? 'trade-plan-override-error' : undefined}
            {...form.register('overrideReason', { setValueAs: optionalText })}
          />
          <FieldError id="trade-plan-override-error" message={errors.overrideReason?.message} />
        </label>
      ) : null}

      <div className="grid gap-2 sm:grid-cols-2">
        {CHECKLIST_ITEMS.map(([key, label]) => (
          <label
            key={key}
            className="flex items-center gap-2 rounded border border-border px-3 py-2 text-sm"
          >
            <input type="checkbox" {...form.register(`checklist.${key}`)} />
            <span>{label}</span>
          </label>
        ))}
      </div>

      {!checklistComplete ? (
        <p className="text-sm text-red-300">Complete every checklist item before submitting.</p>
      ) : null}

      <Button
        type="submit"
        disabled={
          disabled ||
          !form.formState.isValid ||
          !checklistComplete ||
          isSubmitting ||
          accounts.length === 0
        }
      >
        Submit plan
      </Button>
    </form>
  );
}

function FieldError({ id, message }: { id: string; message: string | undefined }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1 text-sm text-danger">
      {message}
    </p>
  );
}
